import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { DollarOutlined } from '@ant-design/icons'
import { Col, Row, Skeleton } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import { uploadFile } from 'core/application/commons/fileStorage/S3FileUploader'
import { getExpenseCategoriesForEmployee } from 'core/application/employeeDashboard/perks/getExpenseCategoriesForEmployee'
import { ExpenseForUpdate, getExpenseForUpdate } from 'core/application/employeeDashboard/perks/getExpenseForUpdate'
import {
  ExpenseTypesByCategoryIdForEmployeeResponse,
  getExpenseTypesByCategoryIdForEmployee,
} from 'core/application/employeeDashboard/perks/getExpenseTypesByCategoryIdForEmployee'
import {
  EmployeeEligibilityInfoResponse,
  getPerksEmployeeEligibilityInfo,
} from 'core/application/employeeDashboard/perks/getPerksEmployeeEligibilityInfo'
import submitPerksExpense, {
  SubmitPerksExpenseRequest,
} from 'core/application/employeeDashboard/perks/submitPerksExpense'
import { UpdatePerksExpenseRequest } from 'core/application/employeeDashboard/perks/updatePerksExpense'
import updatePerksExpense from 'core/application/employeeDashboard/perks/updatePerksExpense'
import { NamedItem } from 'core/domain/common/namedItem'
import { Attachment } from 'core/domain/expenses/attachment'

import Tooltip from 'common/components/dataDisplay/Tooltip'
import Option from 'common/components/dataEntry/components/Option'
import { DatePicker, Input, Select, TextArea } from 'common/components/dataEntry/formik'
import Button from 'common/components/general/Button'

import useCommandRequest from 'common/hooks/useCommandRequest'
import useQueryParams from 'common/hooks/useQueryParams'

import { ROUTE_PATHS } from 'common/routePaths'

import { now, parseZone } from '../../../common/dateFormatters'
import useQueryRequest from '../../../common/hooks/useQueryRequest'
import AttachmentPreview from '../../common/attachmentPreview/AttachmentPreview'
import FileUpload from '../../common/fileUpload/FileUpload'
import Navigation from '../../common/navigation/Navigation'

interface UploadFileProps {
  file: File
  saveForLater?: boolean
  id: number
}

const PerksSubmitExpense: React.FC = () => {
  const history = useHistory()
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])
  const params = useQueryParams()

  const planPeriodId = params.get('planPeriodId') ?? ''
  const paramExpenseCategoryId = params.get('expenseCategoryId') ?? ''
  const expenseId = params.get('expenseId') ?? ''

  const expenseCategoryId = paramExpenseCategoryId ? parseInt(paramExpenseCategoryId) : null

  const [expenseForUpdate, , sendGetExpenseForUpdate] = useQueryRequest<ExpenseForUpdate>(null)

  const [initialValues, setInitialValues] = useState({
    categoryId: expenseCategoryId,
    expenseTypeId: null,
    name: '',
    description: '',
    serviceProvider: '',
    purchasedDate: null,
    requestedAmount: null,
    attachments: [],
  })

  const [hasDirtyData, setHasDirtyData] = useState<boolean>(false)
  const [filesToBeUploaded, setFilesToBeUploaded] = useState<UploadFileProps[]>([])
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([])
  const [sendCommand, isCommandLoading] = useCommandRequest()
  const [categories] = useQueryRequest<NamedItem[]>(null, () => getExpenseCategoriesForEmployee())
  const [expenseTypes, , sendGetExpenseTypesQuery] =
    useQueryRequest<ExpenseTypesByCategoryIdForEmployeeResponse[]>(null)
  const [employeeEligibilityInfo] = useQueryRequest<EmployeeEligibilityInfoResponse>(null, () =>
    getPerksEmployeeEligibilityInfo(planPeriodId),
  )
  const [expenseTypeSelected, setExpenseTypeSelected] = useState<ExpenseTypesByCategoryIdForEmployeeResponse | null>(
    null,
  )
  const [sendUploadFile] = useCommandRequest<any>()
  const [isSubmitting, setIsisSubmitting] = useState(false)

  const onCategorySelectionChanged = async (categoryId) => {
    await sendGetExpenseTypesQuery(() => getExpenseTypesByCategoryIdForEmployee(categoryId))
  }

  useEffect(() => {
    ;(async () => {
      if (expenseCategoryId) {
        await sendGetExpenseTypesQuery(() => getExpenseTypesByCategoryIdForEmployee(expenseCategoryId))
      }

      if (expenseId) {
        await sendGetExpenseForUpdate(() => getExpenseForUpdate(expenseId))
      }
    })()
  }, [expenseCategoryId])

  useEffect(() => {
    ;(async () => {
      if (expenseForUpdate) {
        if (expenseForUpdate.expenseCategoryId) {
          await sendGetExpenseTypesQuery(() =>
            getExpenseTypesByCategoryIdForEmployee(expenseForUpdate.expenseCategoryId),
          )
        }

        setInitialValues({
          ...initialValues,
          categoryId: expenseForUpdate?.expenseCategoryId,
          expenseTypeId: expenseForUpdate?.expenseTypeId,
          name: expenseForUpdate?.name,
          description: expenseForUpdate?.description,
          serviceProvider: expenseForUpdate?.serviceProvider,
          purchasedDate: expenseForUpdate?.purchasedDate,
          requestedAmount: expenseForUpdate?.requestedAmount,
          attachments: expenseForUpdate?.attachments,
        })

        const attachments: Attachment[] = []
        expenseForUpdate.attachments.forEach((item) => {
          attachments.push({
            attachmentId: item.id,
            fileNameUrl: item.fileNameUrl,
            title: item.title,
            reuseInOtherExpenses: item.reuseInOtherExpenses,
          })
        })
        setSelectedAttachments(attachments)
      }
    })()
  }, [expenseForUpdate])

  useEffect(() => {
    ;(async () => {
      if (expenseForUpdate && expenseForUpdate.expenseTypeId) {
        await onExpenseTypeChanged(expenseForUpdate.expenseTypeId)
      }
    })()
  }, [expenseTypes])

  const onExpenseTypeChanged = async (expenseTypeId: number) => {
    const found = expenseTypes.find((x) => x.id == expenseTypeId)
    setExpenseTypeSelected(found)
  }

  const areFilesToBeUploadedValid = () => {
    if (!expenseTypeSelected || !expenseTypeSelected.requireUploadAttachment) return true
    return filesToBeUploaded.length > 0 || selectedAttachments.length > 0
  }

  const onSubmit = async (values: any) => {
    setIsisSubmitting(true)

    const request: SubmitPerksExpenseRequest = {
      ...values,
      planPeriodId: planPeriodId,
      attachments: [],
    }

    Promise.all(
      filesToBeUploaded.map(async (uploadItem) => {
        const result = await sendUploadFile(() => uploadFile(uploadItem.file))

        if (result.completed) {
          request.attachments.push({
            filename: result.data.fileId,
            reuseInOtherExpenses: uploadItem.saveForLater ?? false,
            title: uploadItem.file.name,
          })
        }
      }),
    )
      .then(async () => {
        let result
        if (expenseId) {
          const updateRequest: UpdatePerksExpenseRequest = {
            ...values,
            expenseId: expenseId,
            attachments: selectedAttachments,
          }
          result = await sendCommand(() => updatePerksExpense(updateRequest))
        } else {
          result = await sendCommand(() => submitPerksExpense(request))
        }

        if (result.completed) {
          history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)
          setIsisSubmitting(false)
        }
      })
      .catch((err) => {
        setIsisSubmitting(false)
      })
  }

  return (
    <Navigation
      title={`${translate('get-reimburse.request.perks-get-reimbursed-label')}`}
      redirectPath={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD}
    >
      <Skeleton active loading={!categories}>
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(translate)}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validate={(values) => {
            setHasDirtyData(true)
          }}
        >
          {(formik) => {
            return (
              <form onSubmit={formik.handleSubmit} autoComplete="off" className="reimbursement_request">
                <Row justify="center">
                  <Col xs={24} md={12}>
                    <Row className="mb-16">
                      <Col span={24}>
                        <h5 className="section_title">{translate('get-reimburse.request.expense-details-label')}</h5>
                      </Col>
                    </Row>

                    <Select
                      label={'Perks'}
                      name="categoryId"
                      defaultValue={expenseCategoryId ? [expenseCategoryId] : null}
                      placeholder={'Choose a perk'}
                      onChange={async (value) => {
                        await onCategorySelectionChanged(value)
                        formik.setFieldValue('expenseTypeId', null)
                      }}
                    >
                      {categories &&
                        categories.map((item) => (
                          <Option key={item.id} value={item.id}>
                            {item.name}
                          </Option>
                        ))}
                    </Select>

                    {expenseTypes && expenseTypes.length > 0 && (
                      <Select
                        label={'Expense category'}
                        name="expenseTypeId"
                        defaultValue={null}
                        placeholder={'Choose a category'}
                        onChange={async (value) => {
                          await onExpenseTypeChanged(value)
                        }}
                      >
                        {expenseTypes &&
                          expenseTypes.map((item) => (
                            <Option key={item.id} value={item.id}>
                              {item.name}
                            </Option>
                          ))}
                      </Select>
                    )}

                    <Row>
                      <Col span={24}>
                        <Input label={translate('get-reimburse.request.expense-name')} name="name" />
                      </Col>
                    </Row>

                    <Row>
                      <Col span={24}>
                        <Input
                          label={translate('get-reimburse.request.service-provider-label')}
                          placeholder={translate('get-reimburse.request.service-provider-label')}
                          name="serviceProvider"
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col span={24}>
                        <Input
                          label={translate('get-reimburse.request.expense-amount-label')}
                          name="requestedAmount"
                          prefix={<DollarOutlined />}
                        />
                      </Col>
                    </Row>

                    <Row>
                      <Col span={24}>
                        <DatePicker
                          label={translate('get-reimburse.request.date-of-service-label')}
                          name="purchasedDate"
                          id="purchasedDate"
                          noStyles
                          value={formik.values.purchasedDate ? moment(formik.values.purchasedDate) : null}
                          className="date_of_service_datepicker"
                          placeholder={translate('get-reimburse.request.select-date-label')}
                          disabledDate={(d) => {
                            const date = employeeEligibilityInfo?.terminationDate
                              ? parseZone(employeeEligibilityInfo?.terminationDate)
                              : now()

                            return (
                              !d ||
                              (employeeEligibilityInfo?.benefitEligibilityDate &&
                                d.isBefore(parseZone(employeeEligibilityInfo.benefitEligibilityDate))) ||
                              d.isAfter(date)
                            )
                          }}
                          format={'MM/DD/YYYY'}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <TextArea
                          label={'Notes'}
                          hint={translate('get-reimburse.request.expense-description-hint')}
                          name="description"
                          cols={10}
                          placeholder={translate('get-reimburse.request.expense-description-placeholder')}
                        />
                      </Col>
                    </Row>

                    {expenseTypeSelected && expenseTypeSelected.requireUploadAttachment && (
                      <>
                        <Row className="pt-16 pb-16">
                          <Col span={24}>
                            <h5 className="section_title">
                              {translate('get-reimburse.request.expense-reciept-label')}
                            </h5>
                          </Col>
                        </Row>

                        <Row className="pt-16">
                          <Col span={12} className="pr-24">
                            <FileUpload
                              accept={['.png', '.jpg', '.pdf', '.jpeg']}
                              fileChoosed={(file: File) => {
                                const newList = [...filesToBeUploaded]
                                newList.push({ file: file, id: Date.now() })
                                setFilesToBeUploaded(newList)
                              }}
                              translate={translate}
                            />
                          </Col>
                        </Row>

                        <Row className="pb-16 pt-16">
                          <Col span={24}>
                            {filesToBeUploaded.map((item: UploadFileProps, index: number) => (
                              <AttachmentPreview
                                key={index}
                                uploadedFile={item}
                                deletable
                                onDelete={() => {
                                  const newFileList = [...filesToBeUploaded].filter((file) => file.id !== item.id)
                                  setFilesToBeUploaded(newFileList)
                                }}
                                showSaveForLater
                                onSaveForLaterChange={(checked: boolean) => {
                                  const newList = [...filesToBeUploaded]
                                  const file = newList.find((i) => i.file === item.file)
                                  if (file) file.saveForLater = checked
                                  setFilesToBeUploaded(newList)
                                }}
                                className="mb-8"
                              />
                            ))}

                            {selectedAttachments.map((item: Attachment, index: number) => (
                              <AttachmentPreview
                                key={index}
                                attachment={item}
                                deletable
                                onDelete={() => {
                                  const newFileList = [...selectedAttachments].filter(
                                    (file) => file.attachmentId !== item.attachmentId,
                                  )
                                  setSelectedAttachments(newFileList)
                                }}
                                className="mb-8"
                              />
                            ))}
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row>
                      <Col span={24}>
                        <span className="submit_info">{translate('get-reimburse.request.submit-info')}</span>
                      </Col>
                    </Row>

                    <Row className="pt-16">
                      <Col className="horizontal_line" span={24}></Col>
                    </Row>

                    <Row className="pt-24">
                      <Col span={24} className="bottom_actions">
                        <Button
                          type="default"
                          htmlType="button"
                          onClick={() => {
                            history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)
                          }}
                        >
                          {translate('translation:general.cancel')}
                        </Button>
                        <SubmitButton isValid={formik.isValid && areFilesToBeUploadedValid()} loading={isSubmitting} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </form>
            )
          }}
        </Formik>
      </Skeleton>
    </Navigation>
  )
}

const formValidator = (translate: (key: string) => string) =>
  Yup.object({
    requestedAmount: Yup.number()
      .typeError(translate('get-reimburse.errors.only-numbers'))
      .min(0, translate('get-reimburse.errors.positive-numbers'))
      .required(translate('translation:general.required')),
    purchasedDate: Yup.date()
      .typeError(translate('translation:general.required'))
      .required(translate('translation:general.required')),
    serviceProvider: Yup.string().trim().required(translate('translation:general.required')),
    name: Yup.string().trim().required(translate('translation:general.required')),
    expenseTypeId: Yup.number().nullable().required(translate('translation:general.required')),
    description: Yup.string()
      .nullable()
      .notRequired()
      .trim()
      .transform((value) => (value && value.length > 0 ? value : null)),
  })

type SubmitButtonProps = {
  isValid?: boolean
  loading?: boolean
}
const SubmitButton = (props: SubmitButtonProps) => {
  const { t: translate } = useTranslation(['ee-dashboard', 'translation'])

  const button = (
    <Button type="primary" htmlType="submit" disabled={!props.isValid} loading={props.loading}>
      {translate('get-reimburse.request.submit-expense-label')}
    </Button>
  )

  if (!props.isValid) {
    return (
      <Tooltip title={translate('get-reimburse.request.next-tooltip')} position="left">
        {button}
      </Tooltip>
    )
  } else {
    return button
  }
}

export default PerksSubmitExpense
